import type * as THREE from "three";
import type { Target } from "./targets.ts";

/** Points a leaf of the tree holds, scanned one by one. */
const LEAF = 8;

/**
 * A point set in a k-d tree, for nearest-point distance queries. A query costs about the
 * same near the points or far from them: the scorer asks from every target vertex, and
 * early on (the starting clay far from a long target) most of them are far.
 */
export class PointTree {
  private points: Float32Array = new Float32Array(0);
  /**
   * The points' indices as a tree: a range's median splits it, those before it on the
   * lower side along its axis and those after on the upper.
   */
  private order = new Uint32Array(0);
  /** The splitting axis of the range whose median sits at each position of `order`. */
  private axes = new Uint8Array(0);
  /**
   * The box around each range's points (min x, y, z, max x, y, z), at six times the
   * position of its median: a query skips a range whose box is farther than what it has.
   */
  private boxes = new Float32Array(0);
  /** The nearest squared distance found so far by the query under way. */
  private best = Infinity;

  build(points: Float32Array): void {
    const n = points.length / 3;
    this.points = points;
    this.order = new Uint32Array(n);
    for (let i = 0; i < n; i++) this.order[i] = i;
    this.axes = new Uint8Array(n);
    this.boxes = new Float32Array(n * 6);
    this.split(0, n);
  }

  nearestDistance(x: number, y: number, z: number): number {
    this.best = Infinity;
    this.search(0, this.order.length, x, y, z);
    return Math.sqrt(this.best);
  }

  /**
   * Boxes order[lo, hi), and splits it at its median along the axis the points spread
   * furthest on.
   */
  private split(lo: number, hi: number): void {
    if (lo >= hi) return;
    const { points: p, order, boxes } = this;
    const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (let k = lo; k < hi; k++) {
      for (let a = 0; a < 3; a++) {
        const v = p[order[k] * 3 + a];
        if (v < min[a]) min[a] = v;
        if (v > max[a]) max[a] = v;
      }
    }
    boxes.set([...min, ...max], ((lo + hi) >> 1) * 6);
    if (hi - lo <= LEAF) return;
    const spread = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
    const axis = spread[0] >= spread[1] && spread[0] >= spread[2]
      ? 0
      : spread[1] >= spread[2]
      ? 1
      : 2;
    const mid = (lo + hi) >> 1;
    this.select(lo, hi - 1, mid, axis);
    this.axes[mid] = axis;
    this.split(lo, mid);
    this.split(mid + 1, hi);
  }

  /** Puts the k-th of order[l..r] along `axis` at k, those below it before, above it after. */
  private select(l: number, r: number, k: number, axis: number): void {
    const { points: p, order } = this;
    while (r > l) {
      const pivot = p[order[(l + r) >> 1] * 3 + axis];
      let i = l, j = r;
      while (i <= j) {
        while (p[order[i] * 3 + axis] < pivot) i++;
        while (p[order[j] * 3 + axis] > pivot) j--;
        if (i <= j) {
          const t = order[i];
          order[i++] = order[j];
          order[j--] = t;
        }
      }
      if (k <= j) r = j;
      else if (k >= i) l = i;
      else return;
    }
  }

  private search(lo: number, hi: number, x: number, y: number, z: number): void {
    if (lo >= hi) return;
    const { points: p, order, boxes: b } = this;
    const o = ((lo + hi) >> 1) * 6;
    const bx = Math.max(b[o] - x, 0, x - b[o + 3]), by = Math.max(b[o + 1] - y, 0, y - b[o + 4]);
    const bz = Math.max(b[o + 2] - z, 0, z - b[o + 5]);
    if (bx * bx + by * by + bz * bz >= this.best) return;
    if (hi - lo <= LEAF) {
      for (let k = lo; k < hi; k++) {
        const j = order[k] * 3, dx = p[j] - x, dy = p[j + 1] - y, dz = p[j + 2] - z;
        const d = dx * dx + dy * dy + dz * dz;
        if (d < this.best) this.best = d;
      }
      return;
    }
    const mid = (lo + hi) >> 1, j = order[mid] * 3, axis = this.axes[mid];
    const dx = p[j] - x, dy = p[j + 1] - y, dz = p[j + 2] - z;
    const d = dx * dx + dy * dy + dz * dz;
    if (d < this.best) this.best = d;
    // The side the query is on first; its box keeps the other out if that is farther.
    if ((axis === 0 ? x : axis === 1 ? y : z) < p[j + axis]) {
      this.search(lo, mid, x, y, z);
      this.search(mid + 1, hi, x, y, z);
    } else {
      this.search(mid + 1, hi, x, y, z);
      this.search(lo, mid, x, y, z);
    }
  }
}

/** Compares a clay surface against a target surface. */
export class Scorer {
  private readonly tree = new PointTree();
  private shifted: Float32Array = new Float32Array(0);

  constructor(readonly target: Target) {}

  /**
   * Symmetric, area-weighted mean surface distance after aligning volume centroids
   * (the target is built centred on the origin). Orientation is not aligned.
   */
  distance(positions: Float32Array, areas: Float32Array, centroid: THREE.Vector3): number {
    const target = this.target;
    if (this.shifted.length !== positions.length) this.shifted = new Float32Array(positions.length);
    const shifted = this.shifted;

    // clay → target: distance field of the target at each clay vertex.
    let sum = 0, weight = 0;
    for (let i = 0; i < areas.length; i++) {
      const x = positions[i * 3] - centroid.x;
      const y = positions[i * 3 + 1] - centroid.y;
      const z = positions[i * 3 + 2] - centroid.z;
      shifted[i * 3] = x;
      shifted[i * 3 + 1] = y;
      shifted[i * 3 + 2] = z;
      sum += areas[i] * Math.abs(target.sdf(x, y, z));
      weight += areas[i];
    }
    const clayToTarget = sum / weight;

    // target → clay: nearest clay vertex from each target vertex.
    this.tree.build(shifted);
    const tp = target.positions, ta = target.areas;
    sum = 0;
    weight = 0;
    for (let j = 0; j < ta.length; j++) {
      sum += ta[j] * this.tree.nearestDistance(tp[j * 3], tp[j * 3 + 1], tp[j * 3 + 2]);
      weight += ta[j];
    }
    const targetToClay = sum / weight;

    return 0.5 * (clayToTarget + targetToClay);
  }
}

/**
 * Distance that a perfect match still measures, from vertex sampling (≈0.006 at the
 * current mesh resolutions); it is forgiven so an exact shape counts as 1.
 */
const TOLERANCE = 0.006;

/** How much of the starting distance has been closed: 0 at the start, 1 at a perfect match. */
export function closeness(distance: number, startDistance: number): number {
  const x = Math.max(0, distance - TOLERANCE) / (startDistance - TOLERANCE);
  return Math.min(1, Math.max(0, 1 - x));
}
